import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSubscribeCb } from '@/js/libs/observable/useSubscribe';

import { FALLBACK_COUNTRY, PLACEHOLDER_VALUE } from './constants';
import { useNumberFormatContext } from './context';
import { getFormatter, type Formatter, type NumberFormat } from './formatters';

interface FormattedNumberProps {
  children: number | bigint;
  format: NumberFormat | Formatter;
  currency?: string;
}

// Used for cases when country info not loaded yet
export const placeholderFormatter = { format: () => PLACEHOLDER_VALUE };

export const FormattedNumber = memo(function FormattedNumber({ children, format, currency }: FormattedNumberProps) {
  const { countryRequest } = useNumberFormatContext();
  const {
    i18n: { language },
  } = useTranslation(['backend', 'common']);

  const formatter = useSubscribeCb(
    (ctx) => {
      if (ctx.spy(countryRequest.pending$)) {
        return placeholderFormatter;
      }
      let country;
      if (ctx.spy(countryRequest.error$)) {
        country = FALLBACK_COUNTRY;
      } else {
        country = ctx.spy(countryRequest.data$)?.data ?? FALLBACK_COUNTRY;
      }
      return typeof format === 'string' ? getFormatter(format, country, language, currency) : format(country, currency);
    },
    [language],
  );

  return formatter.format(children);
});

interface FormattedPercentageProps {
  children: number | bigint;
  format: NumberFormat | Formatter;
}

export const FormattedPercentage = memo(function FormattedPercentage({ children, format }: FormattedPercentageProps) {
  return (
    <>
      <FormattedNumber format={format}>{children}</FormattedNumber>%
    </>
  );
});

export { FALLBACK_CURRENCY } from './constants';
